import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { AutoLoginAction } from "@/enums/system/AutoLoginAction";
import { ref } from "vue";
import AiPictureSelectModal from "@/components/ai/ai-picture-select-modal.vue";
import AiPropertyListingWizard from "@/components/properties/ai-property-listing-wizard.vue";
import displayErrorMessage from "@/utils/ErrorHandling";
import { useAiAutoLoginAction } from "@/utils/ai/useAiAutoLoginAction";
import { getErrorMessage } from "bright-logic-core/src/errors";

// Services

export default /*#__PURE__*/_defineComponent({
  __name: 'ai-auto-login-action',
  setup(__props, { expose: __expose }) {

const useAiLoginAction = useAiAutoLoginAction();

// Data
const showAIImageEnhancement = ref(false);
const showAIPropertyListing = ref(false);
const propertyIdRef = ref(0);

// Methods
/** Perform the AI auto action based on the provided action prop */
const performAiAutoAction = async (
  propertyId: number,
  action: AutoLoginAction,
) => {
  propertyIdRef.value = propertyId;

  switch (action) {
    case AutoLoginAction.AIImageEnhancement: {
      /* Due to how the ai modals have been implemented, we only need to lock the property for the image enhancement action
       * As the property listing modal locks the property itself, we don't need to do it here */
      const lockCreated = await lockProperty();

      if (!lockCreated) {
        return;
      }
      showAIImageEnhancement.value = true;
      break;
    }
    case AutoLoginAction.AIPropertyListing:
      showAIPropertyListing.value = true;
      break;
  }
};

/** Lock the property to prevent concurrent modifications while the user is performing the AI action */
const lockProperty = async () => {
  try {
    await useAiLoginAction.lockProperty(propertyIdRef.value, true);
    return true;
  } catch {
    displayErrorMessage(
      "Unable to perform AI action. The property is currently being edited by another user.",
    );
    return false;
  }
};

/** Unlock the property after the user has completed or cancelled the AI action */
const unlockProperty = async () => {
  try {
    await useAiLoginAction.lockProperty(propertyIdRef.value, false);
  } catch (error) {
    displayErrorMessage(getErrorMessage(error));
  }
};

/** Handles the case when no property images are available for enhancement */
const onNoPropertyImagesAvailable = () => {
  displayErrorMessage(
    "Unable to enhance property images. The selected property has no images.",
  );
};

/** Handles the closure of the property listing modal */
const onPropertyListingModalClosed = () => {
  useAiLoginAction.navigateToProperty(propertyIdRef.value);
};

// Expose public methods to allow the parent component to trigger the AI auto action when needed
__expose({
  performAiAutoAction,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(AiPictureSelectModal, {
      "show-modal": showAIImageEnhancement.value,
      "onUpdate:showModal": _cache[0] || (_cache[0] = ($event: any) => ((showAIImageEnhancement).value = $event)),
      propertyId: propertyIdRef.value,
      onNoImagesAvailable: onNoPropertyImagesAvailable,
      onModalClosed: unlockProperty
    }, null, 8, ["show-modal", "propertyId"]),
    _createVNode(AiPropertyListingWizard, {
      modelValue: showAIPropertyListing.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showAIPropertyListing).value = $event)),
      propertyId: propertyIdRef.value,
      onModalClosed: onPropertyListingModalClosed
    }, null, 8, ["modelValue", "propertyId"])
  ], 64))
}
}

})